import React from 'react';
import { lowerCase, upperFirst } from 'lodash';

import { toast } from '@dashboard/context/ToastContext';

import { dateRanges } from '@dashboard/scenes/Dashboard/date-ranges';
import { METRICS } from '@dashboard/scenes/Dashboard/metrics';

import Button from '@dashboard/components/Button';
import { Label } from '@dashboard/components/Form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@dashboard/components/ui/select';

import {
  useStartGlobalStatisticsAggregate,
  useStartGlobalMetricsAggregation,
} from '@dashboard/hooks/queries/useAdminMutations';

import FinalizeStuckFreeBooking from '../components/FinalizeStuckFreeBooking';
import BulkBookingImport from '../components/BulkBookingImport';

export default function AdminActions() {
  const [statisticsRange, setStatisticsRange] = React.useState('lastHour');
  const [metric, setMetric] = React.useState('');

  const startStatisticsAggregate = useStartGlobalStatisticsAggregate({
    onSuccess: () => {
      toast({
        title: 'Statistics aggregation started',
        type: 'success',
      });
    },
    onError: error => {
      toast({
        title: 'Error starting statistics aggregation',
        description: error?.message,
        type: 'error',
      });
    },
  });

  const [metricsRange, setMetricsRange] = React.useState('lastHour');

  const startMetricsAggregation = useStartGlobalMetricsAggregation({
    onSuccess: () => {
      toast({
        title: 'Metrics aggregation started',
        type: 'success',
      });
    },
    onError: error => {
      toast({
        title: 'Error starting metrics aggregation',
        description: error?.message,
        type: 'error',
      });
    },
  });

  return (
    <div className="">
      <div className="mb-4">
        <h1>Actions</h1>
      </div>

      <div className="mt-4">
        <Label>Aggregate historical statistics</Label>
        <div className="flex gap-x-2">
          <div>
            <Select value={statisticsRange} onValueChange={setStatisticsRange}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="lastHour">Last hour</SelectItem>
                {Object.keys(dateRanges).map(range => (
                  <SelectItem value={range} key={range}>
                    {dateRanges[range]?.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Select
              value={metric || 'all'}
              onValueChange={value => setMetric(value === 'all' ? '' : value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All metrics</SelectItem>
                {Object.entries(METRICS).map(([key, value]) => (
                  <SelectItem value={value} key={value}>
                    {upperFirst(lowerCase(key))}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button
            onClick={() => startStatisticsAggregate.mutate({ range: statisticsRange, metric })}
            appearance="primary"
          >
            Start statistics aggregation for all sellers
          </Button>
        </div>
      </div>

      <div className="mt-4">
        <Label>Aggregate historical metrics</Label>
        <div className="flex gap-x-2">
          <div>
            <Select value={metricsRange} onValueChange={setMetricsRange}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="lastHour">Last hour</SelectItem>
                {Object.keys(dateRanges).map(range => (
                  <SelectItem value={range} key={range}>
                    {dateRanges[range]?.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button
            onClick={() => startMetricsAggregation.mutate({ range: metricsRange })}
            appearance="primary"
          >
            Start metrics aggregation for all sellers
          </Button>
        </div>
      </div>

      <FinalizeStuckFreeBooking />

      <BulkBookingImport />
    </div>
  );
}
